import {
  AccountBean,
  EventStatKeyColumnDescription,
  EventStatValueColumnDescription,
  KeyFilterClause,
  ReportRequestParametersObjectObject,
} from 'common/api/api.ts'
import { reportApi } from 'common/api/entitiesApi.tsx'
import { useUserContext } from 'common/context/UserContext.tsx'
import { toTimezone } from 'common/util/Util.tsx'
import dayjs from 'dayjs'
import { useState } from 'react'

import OrderEnum = EventStatKeyColumnDescription.OrderEnum

export interface TopData {
  name: string
  value: number
}

export interface PerformanceData {
  dateTime: string[]
  costs?: number[]
  costGross?: number[]
  costNet?: number[]
  profit?: number[]
  impressions: number[]
  clicks: number[]
  ctr: number[]
  trackers: number[]
  ecpm: number[]
  costSum?: number
  costGrossSum?: number
  costNetSum?: number
  profitSum?: number
  impressionsSum: number
  clicksSum: number
  ctrAvg: number
  trackersSum: number
  ecpmAvg: number
}

export interface DashboardFilters {
  adGroupIds?: number[]
  creativeIds?: number[]
  countryCodes?: string[]
  deviceTypes?: string[]
  sspIds?: number[]
  campaignIds?: number[]
  advertiserIds?: number[]
}

const KeyColumnEnum = EventStatKeyColumnDescription.ColumnEnum
const ValueColumnEnum = EventStatValueColumnDescription.ColumnEnum
const RoleEnum = AccountBean.RoleEnum
type KeyColumnType = EventStatKeyColumnDescription.ColumnEnum
type ValueColumnType = EventStatValueColumnDescription.ColumnEnum

const dateFormat = 'YYYY-MM-DD'

const emptyTopValuesData = (): Record<KeyColumnType, TopData[]> => ({
  [KeyColumnEnum.Advertiser]: [],
  [KeyColumnEnum.Campaign]: [],
  [KeyColumnEnum.Ssp]: [],
  [KeyColumnEnum.Creative]: [],
  [KeyColumnEnum.AdGroup]: [],
  [KeyColumnEnum.Country]: [],
})

function buildFilterKeyColumns(filters?: DashboardFilters): EventStatKeyColumnDescription[] {
  const result: EventStatKeyColumnDescription[] = []

  if (!filters) {
    return result
  }
  if (filters.campaignIds?.length) {
    result.push({
      column: KeyColumnEnum.Campaign,
      where: { type: KeyFilterClause.TypeEnum.In, value: filters.campaignIds },
      onlyFilter: true,
    })
  }
  if (filters.adGroupIds?.length) {
    result.push({
      column: KeyColumnEnum.AdGroup,
      where: { type: KeyFilterClause.TypeEnum.In, value: filters.adGroupIds },
      onlyFilter: true,
    })
  }
  if (filters.creativeIds?.length) {
    result.push({
      column: KeyColumnEnum.Creative,
      where: { type: KeyFilterClause.TypeEnum.In, value: filters.creativeIds },
      onlyFilter: true,
    })
  }
  if (filters.countryCodes?.length) {
    result.push({
      column: KeyColumnEnum.Country,
      where: { type: KeyFilterClause.TypeEnum.In, value: filters.countryCodes },
      onlyFilter: true,
    })
  }
  if (filters.deviceTypes?.length) {
    result.push({
      column: KeyColumnEnum.DeviceType,
      where: { type: KeyFilterClause.TypeEnum.In, value: filters.deviceTypes },
      onlyFilter: true,
    })
  }
  if (filters.sspIds?.length) {
    result.push({
      column: KeyColumnEnum.Ssp,
      where: { type: KeyFilterClause.TypeEnum.In, value: filters.sspIds },
      onlyFilter: true,
    })
  }
  if (filters.advertiserIds?.length) {
    result.push({
      column: KeyColumnEnum.Advertiser,
      where: { type: KeyFilterClause.TypeEnum.In, value: filters.advertiserIds },
      onlyFilter: true,
    })
  }

  return result
}

const asNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, '').trim())
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

export function useDashboardData(dates: any, filters?: DashboardFilters) {
  const { user } = useUserContext()
  const [loading, setLoading] = useState(false)
  const [performanceData, setPerformanceData] = useState<PerformanceData>()
  const [topValuesData, setTopValuesData] = useState<Record<KeyColumnType, TopData[]>>(emptyTopValuesData())

  const resolveDates = () => {
    if (dates.length > 0) {
      return dates
    }
    return [toTimezone(dayjs(), user?.timezoneId), toTimezone(dayjs(), user?.timezoneId), true]
  }

  const loadPerformanceData = async () => {
    setLoading(true)
    try {
      const hasNetCostAccess = user?.role === RoleEnum.Admin || user?.role === RoleEnum.SuperAdmin
      const profitColumn = user?.role === RoleEnum.SuperAdmin ? ValueColumnEnum.Profit : ValueColumnEnum.ProfitAgency
      const datesTZ = resolveDates()
      const keyCols: EventStatKeyColumnDescription[] = [
        {
          column: datesTZ[2] ? KeyColumnEnum.Hour : KeyColumnEnum.Date,
          where: {
            type: KeyFilterClause.TypeEnum.Between,
            value: [datesTZ[0].format(dateFormat), datesTZ[1].format(dateFormat)],
          },
          order: OrderEnum.Asc,
          onlyFilter: false,
        },
        ...buildFilterKeyColumns(filters),
      ]

      const valueCols: EventStatValueColumnDescription[] = [
        { column: ValueColumnEnum.Impression },
        { column: ValueColumnEnum.Click },
        { column: ValueColumnEnum.Event },
        { column: ValueColumnEnum.CostGross },
        ...(hasNetCostAccess
          ? [{ column: ValueColumnEnum.CostNet }, { column: profitColumn }]
          : []),
      ]

      const response = await reportApi.report({
        keyColumns: keyCols,
        valueColumns: valueCols,
        type: ReportRequestParametersObjectObject.TypeEnum.Stats,
      })

      const rows = response?.data?.rows ?? []

      let impressionsSum = 0
      let clicksSum = 0
      let trackersSum = 0
      let costGrossSum = 0
      let costNetSum = 0
      let profitSum = 0

      const dateTime: string[] = []
      const imprsArr: number[] = []
      const clicksArr: number[] = []
      const eventsArr: number[] = []
      const ecpmArr: number[] = []
      const ctrArr: number[] = []
      const costGrossArr: number[] = []
      const costNetArr: number[] = []
      const profitArr: number[] = []

      for (const row of rows) {
        const dateLabel = String(row?.[0] ?? '')
        const imprs = asNumber(row?.[1])
        const clicks = asNumber(row?.[2])
        const events = asNumber(row?.[3])
        const costGross = asNumber(row?.[4])
        const costNet = hasNetCostAccess ? asNumber(row?.[5]) : 0
        const profit = hasNetCostAccess ? asNumber(row?.[6]) : 0
        const ecpm = imprs ? costGross / (imprs / 1000.0) : 0
        const ctr = imprs ? (clicks * 100.0) / imprs : 0

        impressionsSum += imprs
        clicksSum += clicks
        trackersSum += events
        costGrossSum += costGross
        costNetSum += costNet
        profitSum += profit

        dateTime.push(dateLabel)
        imprsArr.push(imprs)
        clicksArr.push(clicks)
        eventsArr.push(events)
        costGrossArr.push(costGross)
        if (hasNetCostAccess) {
          costNetArr.push(costNet)
          profitArr.push(profit)
        }
        ecpmArr.push(ecpm)
        ctrArr.push(ctr)
      }

      const ecpmAvg = impressionsSum ? costGrossSum / (impressionsSum / 1000.0) : 0
      const ctrAvg = impressionsSum ? (clicksSum * 100.0) / impressionsSum : 0

      setPerformanceData({
        dateTime,
        impressions: imprsArr,
        clicks: clicksArr,
        trackers: eventsArr,
        ecpm: ecpmArr,
        ...(hasNetCostAccess
          ? {
              costGross: costGrossArr,
              costNet: costNetArr,
              profit: profitArr,
              costGrossSum,
              costNetSum,
              profitSum,
            }
          : {
              costs: costGrossArr,
              costSum: costGrossSum,
            }),
        ctr: ctrArr,
        impressionsSum,
        clicksSum,
        trackersSum,
        ecpmAvg,
        ctrAvg,
      })
    } finally {
      setLoading(false)
    }
  }

  const loadTopDataByColumn = async (groupColumn: KeyColumnType, valueColumn: ValueColumnType) => {
    const datesTZ = resolveDates()

    const keyCols: EventStatKeyColumnDescription[] = [
      {
        column: datesTZ[2] ? KeyColumnEnum.Hour : KeyColumnEnum.Date,
        where: {
          type: KeyFilterClause.TypeEnum.Between,
          value: [datesTZ[0].format(dateFormat), datesTZ[1].format(dateFormat)],
        },
        onlyFilter: true,
      },
      {
        column: groupColumn,
        onlyFilter: false,
      },
      ...buildFilterKeyColumns(filters),
    ]

    const valueCols: EventStatValueColumnDescription[] = [
      {
        column: valueColumn,
        order: OrderEnum.Desc,
      },
    ]

    const response = await reportApi.report({
      keyColumns: keyCols,
      valueColumns: valueCols,
      type: ReportRequestParametersObjectObject.TypeEnum.Stats,
    })

    const rows = (response?.data?.rows ?? []).slice(0, 10)
    const topData = rows.map((row) => ({
      name: String(row?.[0] ?? ''),
      value: asNumber(row?.[1]),
    }))

    setTopValuesData((prev) => ({ ...prev, [groupColumn]: topData }))
  }

  const loadTopData = async (columns: KeyColumnType[], value: ValueColumnType) => {
    setLoading(true)
    setTopValuesData(emptyTopValuesData())
    try {
      await Promise.all(columns.map((col) => loadTopDataByColumn(col, value)))
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    performanceData,
    topValuesData,
    loadPerformanceData,
    loadTopData,
  }
}
